import { gql } from "graphql-request";

export const FRAGMENT_DIGITAL_RECEPTION_THEME = gql`
  fragment FragmentDigitalReceptionTheme on DigitalReceptionTheme {
    colors {
      primary
      background
      textPrimary
    }
    images {
      hero {
        id
        filename
        absolutePath
      }
      logo {
        id
        filename
        absolutePath
      }
    }
  }
`;

export const FRAGMENT_SCHEDULE = gql`
  fragment FragmentSchedule on Schedule {
    id

    weekdays

    startDate
    startTime

    endDate
    endTime

    disabled
    touchDisabled
  }
`;

export const FRAGMENT_SCREEN_SCHEDULE = gql`
  fragment FragmentScreenSchedule on ScreenSchedule {
    id
    type
    schedule {
      ...FragmentSchedule
    }
  }
  ${FRAGMENT_SCHEDULE}
`;

export const FRAGMENT_PREFERENCE_CHANNEL = gql`
  fragment FragmentPreferenceChannels on MemberGroupPreferenceChannels {
    sms
    call
    ping
    videoCall
    teamsCall
    teamsMessage
    googleChatMessage
  }
`;

export const FRAGMENT_PREFERENCE_AUTO_CHANNEL = gql`
  fragment FragmentPreferenceAutoChannels on MemberGroupPreferenceAutoChannels {
    email
    sms
    call
    ping
    videoCall
    teamsCall
    teamsMessage
    googleChatMessage
  }
`;

export const FRAGMENT_PREFERENCE = gql`
  fragment FragmentPreference on MemberGroupPreference {
    enabled
    channel {
      ...FragmentPreferenceChannels
    }
    autoChannel {
      ...FragmentPreferenceAutoChannels
    }
  }
  ${FRAGMENT_PREFERENCE_CHANNEL}
  ${FRAGMENT_PREFERENCE_AUTO_CHANNEL}
`;

export const FRAGMENT_EMPLOYEE_PREFERENCES = gql`
  fragment FragmentEmployeePreferences on MemberGroupPreferences {
    visitor {
      ...FragmentPreference
    }
    meetingAttendee {
      ...FragmentPreference
    }
    foodDelivery {
      ...FragmentPreference
    }
    packageDelivery {
      ...FragmentPreference
    }
  }
  ${FRAGMENT_PREFERENCE}
`;

export const FRAGMENT_CLIENT_COMPANY_PREFERENCES = gql`
  fragment FragmentClientCompanyPreferences on ClientCompanyPreferences {
    employee {
      ...FragmentEmployeePreferences
    }
  }
  ${FRAGMENT_EMPLOYEE_PREFERENCES}
`;

export const FRAGMENT_CLIENT_PREFERENCES = gql`
  fragment FragmentClientPreferences on ClientPreferences {
    company {
      ...FragmentClientCompanyPreferences
    }
    shelly {
      enabled
    }
    documentScanner {
      enabled
    }
    tuya {
      enabled
    }
  }
  ${FRAGMENT_CLIENT_COMPANY_PREFERENCES}
`;

export const FRAGMENT_OWNER = gql`
  fragment FragmentAccessEventOwner on AccessEventOwner {
    ... on Visitor {
      id
      type
      email
      fullName
    }
    ... on Employee {
      id
      type
      email
      fullName
      availablePreferences {
        ...FragmentEmployeePreferences
      }
    }
    ... on MeetingAttendee {
      id
      email
      fullName
      member {
        type
        email
        fullName
      }
    }
    ... on Guest {
      id
      fields {
        name
        value
      }
    }
  }
  ${FRAGMENT_EMPLOYEE_PREFERENCES}
`;

export const FRAGMENT_ENTRANCE_AGREEMENT_FILE = gql`
  fragment FragmentEntranceAgreementFile on EntranceAgreementFile {
    id
    title
    text
    file {
      id
      absolutePath
      pages {
        id
        absolutePath
      }
    }

    approvalRequired
    disabled

    audience {
      employee {
        enabled
      }
      visitor {
        enabled
      }
      guest {
        enabled
      }
      meetingAttendee {
        enabled
      }
    }

    validFrom
    validUntil
  }
`;

export const FRAGMENT_ENTRANCE_AGREEMENT = gql`
  fragment FragmentEntranceAgreement on EntranceAgreement {
    id
    showFirst
    disabled
    validFrom
    validUntil
    files {
      ...FragmentEntranceAgreementFile
    }
  }
  ${FRAGMENT_ENTRANCE_AGREEMENT_FILE}
`;

export const FRAGMENT_DIGITAL_RECEPTION_LAYOUT_OPTION = gql`
  fragment FragmentDigitalReceptionLayoutOption on DigitalReceptionLayoutOption {
    enabled
    text
    registration {
      id
      fields {
        id
        type
        label
        required
        code
        name
        manualCheckoutIdentifier
        dropdownOptions {
          label
          value
        }
        checkbox {
          description {
            title
            text
          }
          label
        }
        radioButtons {
          buttons {
            label
            value
          }
          hasOther
        }
      }
    }
    isCustom
    instruction
    svg
    callMenu {
      enabled
      options {
        label
        phone
        email
      }
    }
    wayfindingOptions {
      label
      instructionLabel
      svg
      colors {
        background
        text
      }
    }
    bellController {
      id
      timeout
    }
  }
`;

export const FRAGMENT_DIGITAL_RECEPTION_LAYOUT = gql`
  fragment FragmentDigitalReceptionLayout on DigitalReceptionLayout {
    welcomeMessage {
      ...FragmentDigitalReceptionLayoutOption
    }
    checkin {
      ...FragmentDigitalReceptionLayoutOption
    }
    integratedManualCheckin {
      ...FragmentDigitalReceptionLayoutOption
    }
    independentManualCheckin {
      ...FragmentDigitalReceptionLayoutOption
    }
    checkout {
      ...FragmentDigitalReceptionLayoutOption
    }
    manualCheckout {
      ...FragmentDigitalReceptionLayoutOption
    }
    package {
      ...FragmentDigitalReceptionLayoutOption
    }
    food {
      ...FragmentDigitalReceptionLayoutOption
    }
    callReception {
      ...FragmentDigitalReceptionLayoutOption
    }
    overview {
      ...FragmentDigitalReceptionLayoutOption
    }
    wifi {
      ...FragmentDigitalReceptionLayoutOption
    }
    wayfinding {
      ...FragmentDigitalReceptionLayoutOption
    }
    multiWayfinding {
      ...FragmentDigitalReceptionLayoutOption
    }
    bell {
      ...FragmentDigitalReceptionLayoutOption
    }
  }
  ${FRAGMENT_DIGITAL_RECEPTION_LAYOUT_OPTION}
`;

export const FRAGMENT_DIGITAL_RECEPTION_ACCESS = gql`
  fragment FragmentDigitalReceptionAccess on DigitalReceptionAccess {
    enabled
    controller
    checkin {
      enabled
      autoSwitch
      employee {
        enabled
      }
      visitor {
        enabled
      }
      guest {
        enabled
      }
      meetingAttendee {
        enabled
      }
    }
    checkout {
      enabled
      autoSwitch
      employee {
        enabled
      }
      visitor {
        enabled
      }
      guest {
        enabled
      }
      meetingAttendee {
        enabled
      }
    }
  }
`;

export const FRAGMENT_DIGITAL_RECEPTION_SETTINGS = gql`
  fragment FragmentDigitalReceptionSettings on DigitalReceptionSettings {
    support {
      phone
      email
    }
    layout {
      ...FragmentDigitalReceptionLayout
    }
    animations {
      enabled
    }
    printing {
      enabled
      printer
      label {
        width
        height
      }
    }
    receptionistPhone {
      primary
      backup
    }
    receptionistTeams {
      email
      groupId
    }
    call {
      ringDuration
      maxDuration
    }
    deliveryReceiver {
      fullName
      email
      phone
    }
    overview {
      code
      openWithoutCode
      fields
    }
    wifi {
      qrCode
    }
    clock {
      enabled
      opacity
    }
    photoCapture {
      enabled
      employee {
        enabled
        required
      }
      visitor {
        enabled
        required
      }
      meetingAttendee {
        enabled
        required
      }
    }
    contactPreview {
      enabled
    }
    teamsVideoCall {
      enabled
    }
    shelly {
      ...FragmentDigitalReceptionAccess
    }
    scanner {
      enabled
      autoScan {
        enabled
        type
        onlyQrScanner
      }
    }
    nfc {
      enabled
      socketUrl
    }
    documentScanner {
      enabled
      fullName {
        enabled
        allowManualInput
        field
      }
      documentNumber {
        enabled
        allowManualInput
        field
      }
      nationality {
        enabled
        allowManualInput
        field
      }
      gender {
        enabled
        allowManualInput
        field
      }
      expirationDate {
        enabled
        allowManualInput
        field
      }
      personalNumber {
        enabled
        allowManualInput
        field
      }
    }
    tuya {
      ...FragmentDigitalReceptionAccess
    }
  }
  ${FRAGMENT_DIGITAL_RECEPTION_LAYOUT}
  ${FRAGMENT_DIGITAL_RECEPTION_ACCESS}
`;
